import { useMemo, useState } from 'react'
import { useFormat, useI18n } from '@/i18n'
import { isSameIstDay } from '@/lib/ist'
import { sumPaise } from '@/lib/money'
import {
  Banner,
  BilingualLabel,
  Button,
  Card,
  CardBody,
  ChoiceGroup,
  Field,
  Input,
  PhotoCapture,
} from '@/ui'
import type { CommitDeliveryPayload } from '../../commands'
import { ActionBar } from '../../components/GateScreen'
import { nextStep } from '../../model/delivery'
import { tagRanges } from '../../model/slip'
import { FarmerPhoto } from './FindFarmer'
import { useWizard } from './context'
import { useLotFigures } from './hooks'
import { BiText } from '../../components/GateScreen'

/** F4.8 — आपका हिसाब और पर्ची · Your account and slip. Turned to the farmer, in Hindi first. */
export function StepSlip() {
  const { t } = useI18n()
  const fmt = useFormat()
  const { d, dispatch, session, outbox, stamp, startDispute } = useWizard()
  const lots = useLotFigures()
  const [accountMine, setAccountMine] = useState<'YES' | 'NO' | null>(null)
  const [disagree, setDisagree] = useState(false)
  const paper = d.capture_mode === 'PAPER_BACKFILL'
  const total = sumPaise(lots.map((l) => l.figures.amount_paise))
  const agreed = !!d.slip.farmer_agreed_at || paper

  const usedToday = useMemo(() => {
    const s = new Set<string>()
    for (const r of outbox) {
      if (r.type !== 'commit_delivery' || !isSameIstDay(r.created_at, Date.parse(d.started_at)))
        continue
      s.add((r.payload as CommitDeliveryPayload).slip.slip_no.replace(/^0+/, ''))
    }
    return s
  }, [outbox, d.started_at])
  const slipNo = d.slip.slip_no ?? ''
  const slipDup = !paper && slipNo !== '' && usedToday.has(slipNo.replace(/^0+/, ''))
  const ready = agreed && /^\d+$/.test(slipNo) && !slipDup && !!d.slip.photo_media_id

  const answer = (a: string | null) => (a ? t(`gate.answer.${a}` as 'gate.answer.YES') : '—')

  return (
    <div className="flex flex-col gap-4" lang="hi">
      <Card>
        <CardBody className="flex items-center gap-3">
          <FarmerPhoto url={d.farmer.photo_url} size={64} />
          <div className="min-w-0">
            <div className="text-[19px] font-semibold">{d.farmer.name}</div>
            <div className="font-mono text-[13px] text-muted">
              {t('gate.farmer.rosterNo')} {d.farmer.roster_no} · {session.village_name}
            </div>
          </div>
        </CardBody>
      </Card>

      {lots.map(({ plan, bags, figures, card }) => (
        <Card key={plan.id}>
          <CardBody className="flex flex-col gap-1.5 text-[15px]">
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-mono text-[15px] font-semibold">{plan.code}</span>
              <span className={plan.stream === 'MAIN' ? 'text-ok' : 'text-danger'}>
                {t(plan.stream === 'MAIN' ? 'gate.lot.good' : 'gate.lot.red')}
              </span>
            </div>
            <Row label={t('gate.slip.bags')} value={String(plan.bag_count)} />
            <div className="font-mono text-[12px] text-muted">
              {tagRanges(bags.filter((b) => !b.withdrawn).map((b) => b.qr_payload)).join(', ')}
            </div>
            <Row
              label={<BilingualLabel k="gate.slip.weighed" />}
              value={fmt.weight(figures.gross_g)}
            />
            <Row
              label={<BilingualLabel k="gate.slip.sacks" />}
              value={fmt.weight(figures.tare_g)}
            />
            <Row
              label={<BilingualLabel k="gate.slip.produce" />}
              value={fmt.weight(figures.net_g)}
            />
            <Row label={<BilingualLabel k="gate.slip.deducted" />} value={t('gate.price.none')} />
            <Row
              label={<BilingualLabel k="gate.slip.payable" />}
              value={fmt.weight(figures.payable_g)}
              strong
            />
            <Row
              label={<BilingualLabel k="gate.slip.price" />}
              value={card ? `${fmt.money(card.price_paise_per_kg)}/kg` : '—'}
            />
            <Row
              label={<BilingualLabel k="gate.slip.value" />}
              value={fmt.money(figures.amount_paise)}
              strong
            />
          </CardBody>
        </Card>
      ))}

      <Card>
        <CardBody className="flex flex-col gap-2 text-[15px]">
          {lots.length > 1 && <Row label={t('gate.slip.total')} value={fmt.money(total)} strong />}
          <p className="text-[14px]">
            <BilingualLabel k="gate.slip.advance" layout="stacked" />
          </p>
          <p className="text-[14px]">
            <BilingualLabel
              k={d.slip.disputed ? 'gate.slip.toBeSentHeld' : 'gate.slip.toBeSent'}
              layout="stacked"
            />
          </p>
          {d.farmer.bank_verified && d.farmer.account_last4 ? (
            <Row
              label={t('gate.slip.account')}
              value={`•••• ${d.farmer.account_last4} · ${t('gate.slip.verified')}`}
            />
          ) : (
            <Banner tone="error">{t('gate.slip.noAccount')}</Banner>
          )}
          <Row label={t('gate.storage.q1')} value={answer(d.storage.stored_over_7_days)} />
          <Row label={t('gate.storage.q2')} value={answer(d.storage.tablet_or_powder)} />
        </CardBody>
      </Card>

      {!agreed && !disagree && (
        <>
          {d.farmer.bank_verified && (
            <Field label={t('gate.slip.isAccountHis')}>
              <ChoiceGroup
                options={[
                  { value: 'YES', label: t('gate.answer.YES') },
                  { value: 'NO', label: t('gate.answer.NO'), tone: 'warn' },
                ]}
                value={accountMine}
                onChange={setAccountMine}
              />
            </Field>
          )}
          {accountMine === 'NO' && <Banner tone="warn">{t('gate.slip.accountNotHis')}</Banner>}
          <div className="grid grid-cols-2 gap-2">
            <Button size="xl" variant="secondary" onClick={() => setDisagree(true)}>
              <BiText k="gate.slip.disagrees" />
            </Button>
            <Button
              size="xl"
              disabled={d.farmer.bank_verified && accountMine === null}
              onClick={() =>
                dispatch({
                  type: 'farmer_agrees',
                  at: stamp(),
                  account_confirmed: accountMine === 'YES',
                })
              }
            >
              <BiText k="gate.slip.agrees" />
            </Button>
          </div>
        </>
      )}

      {disagree && !agreed && (
        <Card>
          <CardBody className="flex flex-col gap-2">
            <p className="font-semibold">{t('gate.slip.whatDisputed')}</p>
            <Button
              size="touch"
              variant="secondary"
              onClick={() => startDispute({ about: 'WEIGHT' })}
            >
              {t('gate.dispute.about.WEIGHT')}
            </Button>
            {d.failed_bags.length > 0 && (
              <Button
                size="touch"
                variant="secondary"
                onClick={() => startDispute({ about: 'GRADE' })}
              >
                {t('gate.dispute.about.GRADE')}
              </Button>
            )}
            {d.moisture.readings.length > 0 && (
              <Button
                size="touch"
                variant="secondary"
                onClick={() => startDispute({ about: 'MOISTURE' })}
              >
                {t('gate.dispute.about.MOISTURE')}
              </Button>
            )}
            <Button size="touch" variant="ghost" onClick={() => setDisagree(false)}>
              {t('common.back')}
            </Button>
          </CardBody>
        </Card>
      )}

      {agreed && (
        <Card>
          <CardBody className="flex flex-col gap-3">
            {!paper && <Banner tone="info">{t('gate.slip.copyInOrder')}</Banner>}
            <Field
              label={t('gate.slip.slipNo')}
              required
              error={slipDup ? t('gate.slip.slipUsed') : undefined}
            >
              <Input
                size="touch"
                inputMode="numeric"
                className="font-mono"
                value={slipNo}
                disabled={paper}
                onChange={(e) =>
                  dispatch({
                    type: 'slip',
                    patch: { slip_no: e.target.value.replace(/\D/g, '').slice(0, 8) },
                  })
                }
              />
            </Field>
            <Field
              label={t(paper ? 'gate.paper.pagePhoto' : 'gate.slip.photo')}
              required
              hint={paper ? undefined : t('gate.slip.photoHint')}
            >
              <PhotoCapture
                kind="document"
                value={d.slip.photo_media_id}
                onChange={(id) => {
                  dispatch({ type: 'slip', patch: { photo_media_id: id } })
                  if (paper) dispatch({ type: 'paper', patch: { page_photo_media_id: id } })
                }}
              />
            </Field>
          </CardBody>
        </Card>
      )}

      <ActionBar>
        <Button
          size="xl"
          fullWidth
          disabled={!ready}
          onClick={() => dispatch({ type: 'goto', step: nextStep(d, 'slip') })}
        >
          {t('common.next')}
        </Button>
      </ActionBar>
    </div>
  )
}

function Row({
  label,
  value,
  strong,
}: {
  label: React.ReactNode
  value: React.ReactNode
  strong?: boolean
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-rule/50 py-1 last:border-b-0">
      <span className="text-[14px] text-ink-soft">{label}</span>
      <span className={`font-mono tabular ${strong ? 'text-[18px] font-semibold' : 'text-[15px]'}`}>
        {value}
      </span>
    </div>
  )
}
